import type { CSSProperties, ReactNode } from 'react';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type ReorderableListProps = {
  ids: string[];
  disabled?: boolean;
  onReorder: (activeId: string, overId: string) => void | Promise<void>;
  children: ReactNode;
};

// A translated row contributes to its scroll container's overflow bounds. Keep
// it on the vertical axis and inside the list so dragging cannot add scrollbars.
const restrictToVerticalListBounds: Modifier = ({
  activeNodeRect,
  containerNodeRect,
  transform,
}) => {
  if (!activeNodeRect || !containerNodeRect) {
    return { ...transform, x: 0 };
  }

  const minY = containerNodeRect.top - activeNodeRect.top;
  const maxY = containerNodeRect.bottom - activeNodeRect.bottom;

  return {
    ...transform,
    x: 0,
    y: Math.min(Math.max(transform.y, minY), maxY),
  };
};

const reorderModifiers = [restrictToVerticalListBounds];

/**
 * Provides a vertically constrained drag context for the supplied sortable IDs.
 * A reorder is emitted only when an enabled drag ends over a different item;
 * `disabled` is the generic flag for disabling drag handling.
 */
export function ReorderableList({
  ids,
  disabled = false,
  onReorder,
  children,
}: ReorderableListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (disabled || !over || active.id === over.id) {
      return;
    }

    void onReorder(String(active.id), String(over.id));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={reorderModifiers}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}

type SortableItemRenderProps = {
  setNodeRef: (node: HTMLElement | null) => void;
  style: CSSProperties;
  isDragging: boolean;
  dragHandle: ReactNode;
};

type SortableItemProps = {
  id: string;
  disabled?: boolean;
  label: string;
  children: (props: SortableItemRenderProps) => ReactNode;
};

/**
 * Exposes one sortable item's wiring through a render prop.
 * Consumers must apply `setNodeRef` and `style` to the sortable root, render
 * `dragHandle`, and may use `isDragging` for visuals. `disabled` generically
 * disables dragging the item.
 */
export function SortableItem({
  id,
  disabled = false,
  label,
  children,
}: SortableItemProps) {
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : undefined,
    position: 'relative',
    zIndex: isDragging ? 20 : undefined,
  };

  const dragHandle = (
    <Button
      ref={setActivatorNodeRef}
      type="button"
      variant="ghost"
      size="icon"
      className={cn(
        'h-8 w-8 cursor-grab touch-none text-muted-foreground',
        'active:cursor-grabbing',
        isDragging && 'cursor-grabbing'
      )}
      disabled={disabled}
      {...attributes}
      {...listeners}
      aria-label={`Reorder ${label}`}
      onClick={(event) => event.stopPropagation()}
    >
      <GripVertical className="h-4 w-4" />
    </Button>
  );

  return children({ setNodeRef, style, isDragging, dragHandle });
}
